import { Injectable, OnDestroy } from '@angular/core';
import { SvcAppSettings, cookieExist, getCookie, updateLastAccessCookie } from 'projects/lib-shared-common/src/public-api';
import { debounceTime, fromEvent, interval, Subscription } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { AuthService } from './auth.service';
import { AuthUtils } from './auth.utils';
import { debounce } from 'lodash';
import { SvcModule } from 'projects/lib-shared-common/src/lib/settings/enums/svc-module.enum';
import { Router } from '@angular/router';
import { environment } from 'projects/environments/environment';
import { SvcDialogService } from '@solvace/component/svc-dialogs';

@Injectable({
  providedIn: 'root',
})
export class AuthCheckService implements OnDestroy {
  #checkingIsActive = false;
  #authenticated = false;
  #waitingConfirm = false;
  #dateTimeWaitingConfirmShowed: Date;
  #userLastMoveDate: Date = new Date();
  #intervalSubscription: Subscription;
  #mouseSubscription: Subscription;
  #checkInterval = interval(30000);
  #refreshBeforeExpirySeconds = 5 * 60;
  #cookieLegacyIntegrationApi = '2906AC24C7324A2EA1BC2FF27CB08F9A';
  #cookieLastAccess = 'A9673308967047B883713587A240F0DF';
  #cookieTimeout = '399E3DB191D941ACA3B84EBD5B69F167';
  #translationTimeoutSessionMessage = 'Sua sessão irá expirar em 2 minutos. Deseja continuar logado?';
  #translationExpiredSessionMessage = 'Sua sessão não está mais ativa. Você será redirecionado para a tela de login.';
  #debouncedRefreshSessionState = debounce(this.refreshSessionState.bind(this), 500);

  get #getMinutesSinceWaitingConfirmShowed(): number {
    const date = this.#dateTimeWaitingConfirmShowed;
    if (date) {
      let timeDiff = new Date().getTime() - date.getTime();
      let minutes = timeDiff / 60000;
      return minutes;
    }
    return 0;
  }

  public get checkingIsActive(): boolean {
    return this.#checkingIsActive;
  }

  constructor(
    private _dialog: SvcDialogService,
    private _transloco: TranslocoService,
    private _authService: AuthService,
    private _appSettings: SvcAppSettings,
    private _router: Router
  ) {}

  ngOnDestroy(): void {
    this.stopCheckSessionState();
  }

  public startCheckSessionState(): void {
    if (environment.isLocalhost || [SvcModule.Notification, SvcModule.ExternalFeatures, SvcModule.Feed].includes(this._appSettings.module)) return;

    this._authService.check().subscribe((authenticated) => {
      this.#authenticated = authenticated;
      if (this.#authenticated) {
        this.stopCheckSessionState();
        this.#checkingIsActive = true;
        this.#intervalSubscription = this.#checkInterval.subscribe(() => {
          if (this.checkSessionState() && !this.#waitingConfirm) this.refreshAccessTokenBeforeExpiry();
        });
        this.#mouseSubscription = fromEvent(document.body, 'mousemove')
          .pipe(debounceTime(500))
          .subscribe((e) => {
            this.#userLastMoveDate = new Date();
          });
      }
    });
  }

  public stopCheckSessionState(): void {
    this.setWaitingConfirm(false);
    this.#checkingIsActive = false;
    this.#intervalSubscription?.unsubscribe();
    this.#mouseSubscription?.unsubscribe();
  }

  public interceptRequest(): void {
    if (!this.#authenticated || !this.#checkingIsActive) return;
    const dif = new Date().getTime() - this.#userLastMoveDate.getTime();
    if (dif <= 60000) this.#debouncedRefreshSessionState?.();
  }

  public refreshSessionState(options?: { force?: boolean }): void {
    if ((this.#waitingConfirm || !this.#checkingIsActive) && !(options?.force ?? false)) return;
    updateLastAccessCookie(new Date());
  }

  public sessionStateIsValid(): boolean {
    return this.checkSessionState();
  }

  private checkSessionState(): boolean {
    if (this._router?.routerState?.snapshot?.url?.includes('expired-password')) {
      this.stopCheckSessionState();
      return;
    }

    const accessToken = this._authService.accessToken;
    const legacyToken = getCookie(this.#cookieLegacyIntegrationApi);
    const lastAccess = getCookie(this.#cookieLastAccess);
    const idleTimeout = getCookie(this.#cookieTimeout) || '60';

    const killSession = (): void => {
      this._dialog.closeAll();
      this.stopCheckSessionState();
      setTimeout(() => {
        alert(this._transloco.translate(this.#translationExpiredSessionMessage));
        this.killSessionState();
      });
    };

    if (!legacyToken || !accessToken || (cookieExist(this.#cookieLastAccess) && !this.isValidUrlEncodedDate(lastAccess)) || !this.isConvertibleToNumber(idleTimeout)) {
      killSession();
      return false;
    }

    if (!cookieExist(this.#cookieLastAccess)) return true;
    const remaining = this.getRemainingTime(Number(idleTimeout), lastAccess);

    if (this.#waitingConfirm && remaining > 2) {
      this.setWaitingConfirm(false);
      this._dialog.closeAll();
    } else if (this.#waitingConfirm && this.#getMinutesSinceWaitingConfirmShowed >= 1) {
      killSession();
      return false;
    }

    if (remaining > 0 && remaining <= 2 && !this.#waitingConfirm) {
      this.setWaitingConfirm(true);
      this._dialog
        .openConfirm(this._transloco.translate(this.#translationTimeoutSessionMessage), {
          okText: this._transloco.translate('Sim'),
          cancelText: this._transloco.translate('Não'),
          title: this._transloco.translate('Confirmação'),
        })
        .subscribe((confirm) => {
          if (!this.#waitingConfirm) return;
          if (confirm) {
            this.stopCheckSessionState();
            this.refreshSessionState({ force: true });
            this.startCheckSessionState();
          } else {
            this.killSessionState();
          }
        });
      return true;
    }

    if (remaining <= 0) {
      killSession();
      return false;
    }

    return true;
  }

  private refreshAccessTokenBeforeExpiry(): void {
    const accessToken = this._authService.accessToken;
    const expired = AuthUtils.isTokenExpired(accessToken);
    const expiringSoon = AuthUtils.isTokenExpired(accessToken, this.#refreshBeforeExpirySeconds);
    if (!expired && expiringSoon) this._authService.refreshAccessToken().subscribe();
  }

  private killSessionState(): void {
    this.#authenticated = false;
    this.stopCheckSessionState();
    // This fires automatically from the background idle-timeout poller, not from a deliberate
    // "log out" click - only end this app's local session, don't cascade a global Cognito/IdP revoke.
    this._authService.signOut(true, { revokeRemoteSession: false }).subscribe(() => {
      this._router.navigate(['sign-in']);
    });
  }

  private isValidUrlEncodedDate(dateStr: string): boolean {
    const regex = /^\d{2}(%2F|\/)\d{2}(%2F|\/)\d{4}(%2B|%20|\+)\d{2}(%3A|:)\d{2}(%3A|:)\d{2}$/;
    return regex.test(dateStr);
  }

  private isConvertibleToNumber(value: string): boolean {
    const number = parseFloat(value);
    return !isNaN(number) && isFinite(number);
  }

  private getRemainingTime(timeout: number, lastAccess: string): number {
    const dateStr = decodeURIComponent(lastAccess).replace('+', ' ');
    const differenceInMs = new Date().getTime() - this.parseDate(dateStr).getTime();
    return timeout - Math.round(differenceInMs) / (1000 * 60);
  }

  private parseDate(dateStr: string): Date {
    const [day, month, yearAndTime] = dateStr.split('/');
    const [year, time] = yearAndTime.split(' ');
    const [hour, minute, second] = time.split(':');
    return new Date(Date.UTC(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10), parseInt(hour, 10), parseInt(minute, 10), parseInt(second, 10)));
  }

  private setWaitingConfirm(value: boolean): void {
    this.#waitingConfirm = value;
    this.#dateTimeWaitingConfirmShowed = value ? new Date() : null;
  }
}
